import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock3, MapPin, Network, TrendingUp, Users } from "lucide-react";
import BusinessNetworkRegistrationForm from "./BusinessNetworkRegistrationForm";
import EventFlyer from "./components/EventFlyer";
import EventPageMetadata from "./components/EventPageMetadata";
import EventShareActions from "./components/EventShareActions";
import { normaliseBusinessNetworkSource } from "./businessNetworkRegistration";
import {
  businessNetworkEventDateKey,
  businessNetworkEventEndDate,
  formatBusinessNetworkEventAddress,
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
  isBusinessNetworkRegistrationOpen,
  londonDateKey,
} from "./businessNetworkEvents";
import { normalizeEventRecord } from "./eventViewModel";

function registrationSource() {
  if (typeof window === "undefined") return "business-network-event-page";
  return normaliseBusinessNetworkSource(
    new URLSearchParams(window.location.search).get("source") ||
      "business-network-event-page"
  );
}

function mapUrl(event) {
  const address = formatBusinessNetworkEventAddress(event, { includeVenue: true });
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "";
}

export default function BusinessNetworkEventDetail({ event }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);
  const item = normalizeEventRecord(event);
  const registrationOpen = isBusinessNetworkRegistrationOpen(item, now);
  const eventDateKey = businessNetworkEventDateKey(item);
  const eventEnd = businessNetworkEventEndDate(item);
  const eventHasPassed = (eventDateKey && eventDateKey < londonDateKey(now))
    || Boolean(eventEnd && eventEnd.getTime() <= now.getTime());
  const address = formatBusinessNetworkEventAddress(item);
  const directionsUrl = mapUrl(item);

  return (
    <article className="mx-auto max-w-5xl space-y-8" data-content-type="business-network-event">
      <EventPageMetadata event={item} />

      <section
        className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#E63946]/25 via-white/5 to-cyan-500/10 p-5 sm:p-6 md:p-10"
        aria-labelledby="business-network-event-title"
      >
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff9aa2]">
          Urban Unity Business Network · Free to attend
        </p>
        <h1
          id="business-network-event-title"
          className="mt-3 max-w-4xl text-4xl font-black tracking-tight md:text-6xl"
        >
          {item.title}
        </h1>
        <p className="mt-5 text-2xl font-bold text-white">Connect locally. Grow together.</p>
        <p className="mt-2 max-w-3xl text-lg leading-8 text-white/80">
          {item.description ||
            "Meet local businesses, entrepreneurs, professionals and community partners. Build relationships, share opportunities and explore ways to collaborate locally."}
        </p>

        <div className="mt-7 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <Users className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Connect</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <Network className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Collaborate</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <TrendingUp className="h-6 w-6 text-[#ff8b94]" aria-hidden="true" />
            <p className="mt-2 font-semibold">Grow</p>
          </div>
        </div>

        {registrationOpen ? (
          <a
            href="#register"
            className="mt-7 inline-flex min-h-14 w-full items-center justify-center rounded-xl bg-[#E63946] px-6 py-3 text-center text-lg font-black text-white transition hover:bg-[#d62f3d] sm:w-auto"
          >
            Register for this event
          </a>
        ) : null}
      </section>

      <section
        className="grid gap-6 rounded-3xl border border-white/10 bg-white/5 p-5 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:p-8"
        aria-labelledby="business-network-event-details-title"
      >
        <EventFlyer event={item} className="self-start" eager />
        <div className="self-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff9aa2]">
            {registrationOpen ? "Registration open" : eventHasPassed ? "Previous event" : "Event details"}
          </p>
          <h2 id="business-network-event-details-title" className="mt-2 text-3xl font-black md:text-4xl">
            Event details
          </h2>
          <dl className="mt-6 grid gap-4">
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <CalendarDays className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div>
                <dt className="font-bold">Date</dt>
                <dd className="mt-1 text-white/75">
                  <time dateTime={eventDateKey}>{formatBusinessNetworkEventDate(item)}</time>
                </dd>
              </div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <Clock3 className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div>
                <dt className="font-bold">Time</dt>
                <dd className="mt-1 text-white/75">{formatBusinessNetworkEventTime(item)}</dd>
              </div>
            </div>
            <div className="flex gap-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-[#ff8b94]" aria-hidden="true" />
              <div>
                <dt className="font-bold">Venue</dt>
                <dd className="mt-1 text-white/75">
                  {item.venueName || "Venue to be confirmed"}
                  {address ? <><br />{address}</> : null}
                </dd>
                {directionsUrl ? (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex font-bold text-[#ff9aa2] underline-offset-4 hover:underline"
                  >
                    View on Google Maps
                  </a>
                ) : null}
              </div>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-6 text-white/65">
            Questions? Email{" "}
            <a
              href={`mailto:info@urbanunitynetwork.com?subject=${encodeURIComponent(item.title)}`}
              className="font-bold text-[#ff9aa2] underline-offset-4 hover:underline"
            >
              info@urbanunitynetwork.com
            </a>.
          </p>
        </div>
      </section>

      {registrationOpen ? (
        <BusinessNetworkRegistrationForm
          key={item.id}
          event={item}
          source={registrationSource()}
        />
      ) : (
        <section className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center md:p-8">
          <h2 className="text-2xl font-black">
            {eventHasPassed ? "This event has now taken place." : "Registration is not currently open."}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl leading-7 text-white/70">
            Visit the permanent Business Network page to see the next published event and previous highlights.
          </p>
          <Link
            to="/business-network"
            className="mt-5 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#E63946] px-5 py-3 font-bold text-white"
          >
            View the Business Network
          </Link>
        </section>
      )}

      <EventShareActions event={item} />
    </article>
  );
}
